import { Link } from "react-router-dom";

/**
 * Small legal reminder that sits under any primary CTA
 * ("Get a free quote" / "Submit inquiry" / etc).
 *
 * - Desktop (md+): absolutely-positioned tooltip that fades in when the parent
 *   `.group` (i.e., the CTA container) is hovered.
 * - Mobile (< md): always-visible inline hint below the button.
 *
 * Usage:
 *   <div className="relative inline-flex flex-col group">
 *     <Link className="btn-primary">Get a free quote</Link>
 *     <TermsHint />
 *   </div>
 *
 * Set `variant="dark"` when placed on a dark background so the tooltip inverts.
 */
export default function TermsHint({
  variant = "light",
  align = "left",
  dataTestId = "terms-hint",
  hideMobile = false,
}) {
  const dark = variant === "dark";
  const mobileText = dark ? "text-white/60" : "text-[#5B6472]";
  const mobileLink = dark ? "text-white/85 underline" : "text-[#0B2545] underline";
  const tipBg = dark ? "bg-white text-[#0B2545]" : "bg-[#0B2545] text-white";
  const tipLink = dark ? "text-[#0B2545] underline" : "text-white underline";
  const alignClass =
    align === "center" ? "left-1/2 -translate-x-1/2" :
    align === "right"  ? "right-0" : "left-0";

  const inner = (
    <>
      By clicking, you agree to our{" "}
      <Link
        to="/terms-conditions"
        className={`${mobileLink} underline-offset-2 hover:opacity-80`}
        data-testid={`${dataTestId}-mobile-link`}
      >
        Terms &amp; Conditions
      </Link>
      .
    </>
  );

  return (
    <>
      {/* Mobile — always visible inline hint (unless suppressed by parent) */}
      {!hideMobile && (
        <p
          className={`md:hidden mt-2 text-[10px] leading-snug ${mobileText}`}
          data-testid={`${dataTestId}-mobile`}
        >
          {inner}
        </p>
      )}

      {/* Desktop — hover tooltip */}
      <div
        className={`hidden md:block absolute top-full ${alignClass} mt-2 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity duration-200 z-30`}
        data-testid={`${dataTestId}-tooltip`}
      >
        <div className={`relative ${tipBg} text-xs px-3.5 py-2 whitespace-nowrap shadow-lg`}>
          <div
            className={`absolute -top-1 ${align === "center" ? "left-1/2 -translate-x-1/2" : align === "right" ? "right-4" : "left-4"} w-2 h-2 rotate-45 ${tipBg.split(" ")[0]}`}
          />
          By clicking, you agree to our{" "}
          <Link to="/terms-conditions" className={`${tipLink} underline-offset-2 hover:opacity-80`}>
            Terms &amp; Conditions
          </Link>
          .
        </div>
      </div>
    </>
  );
}
